import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const plans = [
  {
    name: 'Starter',
    price: 'Free',
    period: 'forever',
    description: 'Perfect for exploring Five AI',
    features: [
      '3 AI coaching sessions',
      'Basic resume optimizer',
      '5 daily nudges',
      'Progress tracking',
    ],
    cta: 'Start Free',
    highlighted: false,
  },
  {
    name: 'Professional',
    price: '$29',
    period: 'per month',
    description: 'For serious career growth',
    features: [
      'Unlimited AI coaching',
      'Advanced resume & outreach tools',
      'Unlimited daily nudges',
      'Full Progress Vault access',
      'Communication enhancer',
      'Priority support',
      'Career roadmap builder',
    ],
    cta: 'Start 7-Day Trial',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    description: 'For teams and organizations',
    features: [
      'Everything in Professional',
      'Team dashboards',
      'Custom integrations',
      'Dedicated account manager',
      'SSO & advanced security',
      'Custom training',
    ],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

export const Pricing = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent dark:via-primary/[0.06]" />
      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            <span className="text-foreground">Ready to </span>
            <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
              Transform Your Career?
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join thousands of professionals who are already using Five to
            accelerate their career growth
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={plan.highlighted ? 'md:-mt-4' : ''}
            >
              <Card
                className={`p-8 h-full rounded-2xl relative border-2 transition-all duration-300 ${
                  plan.highlighted
                    ? 'border-primary shadow-2xl shadow-primary/10 bg-gradient-to-br from-background to-primary/10 hover:border-primary/80'
                    : 'border-border bg-gradient-to-br from-primary/[0.02] to-violet-500/[0.02] hover:border-primary/30'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 text-primary-foreground px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1 shadow-lg shadow-primary/20">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {plan.description}
                  </p>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.period && (
                      <span className="text-muted-foreground ml-2">
                        /{plan.period}
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/onboarding')}
                  className={`w-full mb-6 rounded-xl ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 hover:opacity-90 shadow-lg shadow-primary/20 border-0'
                      : 'border-2'
                  }`}
                  variant={plan.highlighted ? 'primary' : 'outline'}
                  size="lg"
                >
                  {plan.cta}
                </Button>

                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

